
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import CommonDialog from "@/components/common/CommonDialog.vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import { MypageHistoryRecipes, HTMLDivElementEvent } from "@/types/mypage";
import { CommonRecipe } from "@/types/commonRecipe";
import {
  isEndOfScrollBottom,
  createFavoriteHistoryRecipesToCommonRecipes,
  createFavoriteSendedHistoryRecipesToCommonRecipes
} from "@/utils/favorites-util";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
  name: "MypageHistoryRecipePage",
  components: { CommonRecipeList, CommonDesignDialog },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    const titleInfo = new TitleInfo(false, "閲覧履歴");
    NativeClient.setTitle(titleInfo);
    CommonUtil.scroll({ top: 0, left: 0 });

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      NativeClient.sendDebugLog("MypageHistoryRecipePage updateDisplay");
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    //
    // API実行
    //
    let isRequesting = false;
    const getRecipeList = async () => {
      NativeClient.sendDebugLog("MypageHistoryRecipePage getRecipeList");
      store.dispatch("common/startLoading");
      isRequesting = true;
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        // 閲覧履歴
        await store.dispatch("mypage/getHistoryRecipe");
      }
      store.dispatch("common/stopLoading");
      isRequesting = false;
      updateDisplay();
    };

    // 更新
    const updatePage = (info: UpdatePageInfo) => {
      if (info.viewedHistory) {
        NativeClient.sendDebugLog("MypageHistoryRecipePage updatePage");
        store.dispatch("mypage/clearHistoryRecipe");
        getRecipeList();
      }
    }

    // ビーコンログ出力
    const sendBeaconLog = () => {
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_DISPLAYED_LIST, null, []);
      NativeClient.sendViewedLog(logInfo);
    };

    // スクロール検知
    const onScroll = (event: HTMLDivElementEvent) => {
      const historyRecipeList: MypageHistoryRecipes = store.getters["mypage/getHistoryRecipe"];
      if (historyRecipeList != null) {
        const recipeCount = historyRecipeList.recipes.length;
        const totalCount = historyRecipeList.totalCount;
        //NativeClient.sendDebugLog("HistoryRecipeListPage onScroll detect recipeCount = " + recipeCount + ", totalCount = " + totalCount);
        if (
          isEndOfScrollBottom(event) &&
          (recipeCount < totalCount) &&
          !isRequesting
        ) {
          NativeClient.sendDebugLog("HistoryRecipeListPage onScroll detect");
          getRecipeList();
        }
      }
    };

    // 本ページの表示／非表示
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;
      NativeClient.sendDebugLog("MypageHistoryRecipePage onMounted");

      // マイリスト更新
      NativeClient.registUpdatePageListener(updatePage);

      sendBeaconLog();

      getRecipeList();
    });
    onUnmounted(() => {
      isDisplayed = false;

      store.dispatch("mypage/clearHistoryRecipe");
    });

    // レシピリストを取得
    const recipeList = computed(() => {
      let recipeList: CommonRecipe[] = [];
      NativeClient.sendDebugLog("HistoryRecipeListPage recipeList getViewedHistory");
      const historyRecipeList: MypageHistoryRecipes = store.getters["mypage/getHistoryRecipe"];
      if (historyRecipeList != null) {
        recipeList = createFavoriteHistoryRecipesToCommonRecipes(historyRecipeList.recipes)
      } else {
        NativeClient.sendDebugLog("HistoryRecipeListPage recipeList getHistoryRecipe is Null");
        return null;
      }
      return recipeList;
    });

    // レシピ選択
    const selectRecipe = (recipeId: number, type: string) => {
      NativeClient.sendDebugLog("HistoryRecipeListPage selectRecipe");
      NativeClient.transitionReceipDetail(recipeId, type);
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      NativeClient.sendDebugLog("MypagePage retryApiErrorHandler");
      closeDialog();
      // isRequesting = false;
      // getRecipeList();
    };
    const okApiErrorHandler = () => {
      NativeClient.sendDebugLog("MypagePage okApiErrorHandler");
      isRequesting = false;
      closeDialog();
    };

    return {
      onScroll,
      recipeList,
      updateDisplayKey,
      selectRecipe,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      okButtonTitle,
      retryButtonTitle,
      okApiErrorHandler,
      retryApiErrorHandler
    };
  }
});
